import { useState } from "react";
import { Search } from "lucide-react";
import { day, money, STATUS_STYLE } from "@/lib/money";

const FILTERS = [["all", "All"], ["paying", "Paying"], ["free", "No plan"], ["ended", "Cancelled / ending"]];
const ENDED = ["canceled", "unpaid", "incomplete_expired"];
const match = (c, f) => (f === "all" ? true : f === "free" ? c.status === "free" : f === "ended" ? ENDED.includes(c.status) || c.status === "canceling" : c.status !== "free" && !ENDED.includes(c.status));

export default function ClientPlansTable({ clients }) {
  const [q, setQ] = useState("");
  const [f, setF] = useState("all");
  const term = q.toLowerCase();
  const rows = clients.filter((c) => match(c, f) && [c.name, c.email, c.business, c.plan].join(" ").toLowerCase().includes(term));
  return (
    <section className="brutal-border mb-8 min-w-0" data-testid="revenue-clients">
      <div className="p-4 sm:p-5 flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-[#262626]">
        <div className="font-mono-label text-[10px] text-muted-foreground">CLIENTS & PLANS · {rows.length}</div>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex">
            {FILTERS.map(([k, l]) => (
              <button key={k} onClick={() => setF(k)} data-testid={`revenue-filter-${k}`} aria-pressed={f === k}
                className={`min-h-[40px] px-3 font-mono-label text-[10px] border -ml-px first:ml-0 transition-colors ${f === k ? "border-[#FFD600] text-[#FFD600] relative z-10" : "border-[#262626] hover:text-white text-muted-foreground"}`}>{l}</button>
            ))}
          </div>
          <div className="relative sm:w-64">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client or plan…" data-testid="revenue-search"
              className="w-full min-h-[40px] bg-[#141414] border border-[#262626] pl-9 pr-3 text-base sm:text-sm focus:outline-none focus:border-white" />
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead className="font-mono-label text-[10px] text-muted-foreground text-left">
            <tr className="border-b border-[#262626]">
              {["Client", "Plan", "Status", "Started", "Renews / ends", "Monthly", "Paid", "Refunded", "Discounts", "Net"].map((h) => (
                <th key={h} className="px-4 py-2.5 font-normal whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.user_id} className="border-b border-[#1A1A1A] hover:bg-[#101010]" data-testid={`revenue-client-${c.user_id}`}>
                <td className="px-4 py-2.5 max-w-[220px]">
                  <div className="font-semibold truncate">{c.name || c.email || "Unnamed"}</div>
                  <div className="text-xs text-muted-foreground truncate">{[c.business, c.email].filter(Boolean).join(" · ")}</div>
                </td>
                <td className="px-4 py-2.5 whitespace-nowrap" data-testid={`revenue-client-plan-${c.user_id}`}>{c.plan}</td>
                <td className={`px-4 py-2.5 whitespace-nowrap font-mono-label text-[10px] ${STATUS_STYLE[c.status] || ""}`} data-testid={`revenue-client-status-${c.user_id}`}>
                  {c.status === "free" ? "NO PLAN" : c.status.replace("_", " ").toUpperCase()}
                </td>
                <td className="px-4 py-2.5 whitespace-nowrap text-muted-foreground">{day(c.started)}</td>
                <td className="px-4 py-2.5 whitespace-nowrap text-muted-foreground">{c.kind === "subscription" ? day(c.renews_at) : "—"}</td>
                <td className="px-4 py-2.5 whitespace-nowrap" data-testid={`revenue-client-mrr-${c.user_id}`}>{c.mrr_cents ? money(c.mrr_cents) : "—"}</td>
                <td className="px-4 py-2.5 whitespace-nowrap">{money(c.paid_cents)}</td>
                <td className={`px-4 py-2.5 whitespace-nowrap ${c.refunded_cents ? "text-[#F87171]" : "text-muted-foreground"}`}>{money(c.refunded_cents)}</td>
                <td className="px-4 py-2.5 whitespace-nowrap text-muted-foreground">{money(c.discounts_cents)}</td>
                <td className="px-4 py-2.5 whitespace-nowrap font-semibold">{money(c.net_cents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <p className="p-6 text-sm text-muted-foreground" data-testid="revenue-clients-empty">No clients match.</p>}
      </div>
    </section>
  );
}
